import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { Minus, Plus } from 'lucide-react';

import type { ToastGuidGroup } from '@Types/toastGuidGroup';
import type { ToastGuid } from '@Types/toastGuid';
import { useToastGuids } from '@Hooks/useToastGuids';
import { useVenues } from '@Hooks/useVenues';
import { useCreateToastGuidGroup } from '@Hooks/useCreateToastGuidGroup';
import { useUpdateToastGuidGroup } from '@Hooks/useUpdateToastGuidGroup';
import { useDeleteToastGuid } from '@Hooks/useDeleteToastGuid';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@Components/AlertDialog';
import { GroupedMultiSelectCombobox } from '../GroupedMultiSelectCombobox';
import { Combobox } from '../Combobox';

const NewToastGuidSchema = z.object({
  name: z.string().optional(),
  resourceGuid: z.string().min(1, 'GUID is required'),
  venueId: z.string().min(1, 'Venue is required'),
});

const FormSchema = z.object({
  name: z.string().min(1, {
    message: 'Name is required.',
  }),
  description: z
    .string()
    .optional()
    .transform((val) => (val && val.trim().length ? val.trim() : null)),
  toastGuidIds: z.array(z.string()).optional(),
  newToastGuids: z.array(NewToastGuidSchema).optional(),
});

type FormValues = z.infer<typeof FormSchema>;

export interface ToastGuidGroupSheetProps {
  toastGuidGroup?: ToastGuidGroup | null;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
  onSuccess?: () => void;
}

export default function ToastGuidGroupSheet({
  toastGuidGroup,
  isOpen,
  setIsOpen,
  onSuccess,
}: ToastGuidGroupSheetProps) {
  const isEditMode = !!toastGuidGroup;

  const createMutation = useCreateToastGuidGroup();
  const updateMutation = useUpdateToastGuidGroup();
  const deleteMutation = useDeleteToastGuid();

  const existingToastGuidIds = useMemo(() => {
    if (!toastGuidGroup?.guids) return [];
    return toastGuidGroup.guids
      .filter((tg) => !tg.deletedAt)
      .map((tg) => tg.guidId);
  }, [toastGuidGroup]);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: toastGuidGroup?.name ?? '',
      description: toastGuidGroup?.description ?? '',
      toastGuidIds: existingToastGuidIds,
      newToastGuids: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'newToastGuids',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toastGuidToDelete, setToastGuidToDelete] = useState<ToastGuid | null>(
    null,
  );

  const toastGuids = useToastGuids({
    searchParams: { 'filter[deletedAt]': 'null', 'page[size]': '-1' },
    enabled: isOpen,
  });

  const venues = useVenues({
    enabled: isOpen,
    pageSize: -1,
  });

  const toastGuidGroups = useMemo(() => {
    const guids = toastGuids.data?.data ?? [];
    const venueMap = new Map((venues.data ?? []).map((v) => [v.id, v.name]));

    const grouped = new Map<string, { value: string; label: string }[]>();
    guids.forEach((g) => {
      const venueName = venueMap.get(g.venueId) ?? 'Unknown Venue';
      if (!grouped.has(venueName)) {
        grouped.set(venueName, []);
      }
      grouped.get(venueName)!.push({
        value: g.id,
        label: g.name ?? g.guid ?? g.id,
      });
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, items]) => ({
        label,
        items: items.sort((a, b) => a.label.localeCompare(b.label)),
      }));
  }, [toastGuids.data, venues.data]);

  const venueItems = useMemo(() => {
    return (venues.data ?? [])
      .map((venue) => ({
        value: venue.id,
        label: venue.name,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
      );
  }, [venues.data]);

  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: toastGuidGroup?.name ?? '',
        description: toastGuidGroup?.description ?? '',
        toastGuidIds: existingToastGuidIds,
        newToastGuids: [],
      });
    }
  }, [toastGuidGroup, isOpen, form, existingToastGuidIds]);

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');

    const newToastGuidsPayload =
      values.newToastGuids && values.newToastGuids.length > 0
        ? values.newToastGuids.map((tg) => ({
            name: tg.name || undefined,
            resourceGuid: tg.resourceGuid,
            venueId: tg.venueId,
          }))
        : undefined;

    try {
      if (isEditMode && toastGuidGroup) {
        await updateMutation.mutateAsync({
          id: toastGuidGroup.id,
          payload: {
            name: values.name,
            description: values.description,
            toastGuidIds: values.toastGuidIds,
            toastGuids: newToastGuidsPayload,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: values.name,
          description: values.description,
          toastGuidIds: values.toastGuidIds,
          toastGuids: newToastGuidsPayload,
        });
      }

      onSuccess?.();
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving toast guid group:', error);
      form.setError('root', {
        type: 'server',
        message:
          error instanceof Error
            ? error.message
            : 'We were unable to save this Toast GUID Group. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      form.reset({
        name: toastGuidGroup?.name ?? '',
        description: toastGuidGroup?.description ?? '',
        toastGuidIds: existingToastGuidIds,
        newToastGuids: [],
      });
    }
  };

  const handleAddNewGuid = () => {
    append({ name: '', resourceGuid: '', venueId: '' });
  };

  const handleDeleteConfirm = async () => {
    if (!toastGuidToDelete) return;

    try {
      await deleteMutation.mutateAsync({ id: toastGuidToDelete.id });
      // Remove from selected values
      const currentValues = form.getValues('toastGuidIds') ?? [];
      form.setValue(
        'toastGuidIds',
        currentValues.filter((id) => id !== toastGuidToDelete.id),
      );
      toastGuids.refetch();
      setDeleteDialogOpen(false);
      setToastGuidToDelete(null);
    } catch (error) {
      console.error('Error deleting toast guid:', error);
    }
  };

  const rootError = form.formState.errors.root?.message;
  const isSubmitting =
    form.formState.isSubmitting ||
    createMutation.isPending ||
    updateMutation.isPending;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>
                  {isEditMode
                    ? `Edit ${toastGuidGroup.name}`
                    : 'Create Toast GUID Group'}
                </SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. VIP Restaurants"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          A friendly name for this Toast GUID Group.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Optional description..."
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <FormField
                    control={form.control}
                    name="toastGuidIds"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Existing Toast GUIDs</FormLabel>
                        <GroupedMultiSelectCombobox
                          groups={toastGuidGroups}
                          selectedValues={field.value ?? []}
                          onChange={field.onChange}
                          placeholder="Select existing Toast GUIDs..."
                          disabled={
                            toastGuids.isLoading ||
                            !toastGuids.data?.data?.length
                          }
                          loading={toastGuids.isLoading}
                        />
                        <FormDescription>
                          Select existing Toast GUIDs to include in this group.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Separator />

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-base">
                        Add New Toast GUIDs
                      </FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddNewGuid}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        Add
                      </Button>
                    </div>

                    {fields.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        No new Toast GUIDs added. Click "Add" to create new
                        ones.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {fields.map((field, index) => (
                          <div
                            key={field.id}
                            className="space-y-4 rounded-md border p-4"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">
                                New Toast GUID #{index + 1}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => remove(index)}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                            </div>

                            <FormField
                              control={form.control}
                              name={`newToastGuids.${index}.name`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Name</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="e.g. Dining Room Menu"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormDescription>
                                    Optional friendly name for this Toast GUID.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`newToastGuids.${index}.resourceGuid`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Resource GUID</FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="e.g. 12345678-1234-1234-1234-123456789012"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`newToastGuids.${index}.venueId`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Venue</FormLabel>
                                  <Combobox
                                    inForm
                                    id={`newToastGuids.${index}.venueId`}
                                    items={venueItems}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Select a venue"
                                    disabled={
                                      venues.isLoading || !venues.data?.length
                                    }
                                    loading={venues.isLoading}
                                    inModal
                                  />
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </SheetBody>

              <SheetFooter>
                <Button type="submit" variant="primary" loading={isSubmitting}>
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Toast GUID</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this Toast GUID? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
